import { Checkbox, Tag } from "antd";
import { useEffect, useRef, useState } from "react";
import { Button as ZhButton, Dialog as ZhDialog, Input as ZhInput } from "zhihao-ui-react";

import request from "@/api";
import type { SystemRole, SystemRolePageResult, SystemRoleQuery } from "../role/types";

export interface BackendAccountRoleSelectorProps {
    value?: number[];
    onChange?: (value: number[]) => void;
    loadRoles?: (query: SystemRoleQuery) => Promise<SystemRolePageResult>;
    editMode?: boolean;
}

/** 复用 Vue 角色分页接口，为后台用户表单提供远程授权角色选择。 */
export async function loadBackendAccountRoles(query: SystemRoleQuery): Promise<SystemRolePageResult> {
    const response = await request.service.get<SystemRolePageResult>("/admin/role/paging", { params: query });
    return response.data;
}

function getRoleId(role: SystemRole): number | undefined {
    if (role.id == null) return undefined;
    const id = Number(role.id);
    return Number.isFinite(id) ? id : undefined;
}

/** 后台用户授权角色选择器，保留 Vue 的搜索、勾选和已选回显语义。 */
export function BackendAccountRoleSelector({
    value = [],
    onChange,
    loadRoles = loadBackendAccountRoles,
    editMode = false
}: BackendAccountRoleSelectorProps) {
    const [roleName, setRoleName] = useState("");
    const [roles, setRoles] = useState<SystemRole[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draftIds, setDraftIds] = useState<number[]>(value);
    const lastValueRef = useRef<number[]>(value);

    const searchRoles = async (name = roleName) => {
        try {
            const result = await loadRoles({ page: 1, size: 10000, name });
            setRoles(result.list || []);
        } catch {
            setRoles([]);
        }
    };

    useEffect(() => {
        let disposed = false;
        void loadRoles({ page: 1, size: 10000, name: "" })
            .then(result => {
                if (!disposed) setRoles(result.list || []);
            })
            .catch(() => {
                if (!disposed) setRoles([]);
            });
        return () => {
            disposed = true;
        };
    }, [loadRoles]);

    useEffect(() => {
        // 编辑页异步回填表单后同步暂存值，确保弹窗勾选状态与 Vue 详情回显一致。
        const lastValue = lastValueRef.current;
        const valueChanged = lastValue.length !== value.length || lastValue.some((id, index) => id !== value[index]);
        if (!valueChanged) return;
        lastValueRef.current = value;
        setDraftIds(value);
    }, [value]);

    const selectedIds = new Set(draftIds);
    const selectedRoles = value
        .map(id => roles.find(role => getRoleId(role) === id))
        .filter((role): role is SystemRole => Boolean(role));
    const draftRoles = draftIds
        .map(id => roles.find(role => getRoleId(role) === id))
        .filter((role): role is SystemRole => Boolean(role));

    const toggleRole = (role: SystemRole, checked: boolean) => {
        const id = getRoleId(role);
        if (id == null) return;
        const next = new Set(selectedIds);
        if (checked) next.add(id);
        else next.delete(id);
        setDraftIds([...next]);
    };

    const removeRole = (role: SystemRole) => {
        const id = getRoleId(role);
        if (id == null) return;
        setDraftIds(draftIds.filter(item => item !== id));
    };

    /** 打开弹窗时复制外部值，取消不会污染表单，保持 Vue Dialog 的暂存语义。 */
    const openDialog = () => {
        setDraftIds(value);
        setDialogOpen(true);
    };
    const saveDialog = () => {
        onChange?.(draftIds);
        setDialogOpen(false);
    };

    return (
        <div aria-label="backend-account-role-selector" className="backend-account-role-selector">
            <div aria-label="已选择的角色" className="backend-account-role-selector__tags">
                {selectedRoles.map(role => (
                    <Tag
                        className="backend-account-role-selector__tag"
                        key={getRoleId(role)}
                        closable
                        onClose={() => onChange?.(value.filter(id => id !== getRoleId(role)))}
                    >
                        {role.name || "--"}
                    </Tag>
                ))}
                {!selectedRoles.length ? (
                    <span className="backend-account-role-selector__placeholder">请选择授权角色</span>
                ) : null}
            </div>
            <button
                aria-label="选择角色"
                className="backend-account-role-selector__trigger"
                type="button"
                onClick={openDialog}
            >
                {/* 直接复用 Vue 旧版 iconfont，保证新增和编辑场景使用原有图标资源。 */}
                <i aria-hidden="true" className={`iconfont ${editMode ? "icon-edit" : "icon-add-person"}`} />
            </button>
            <ZhDialog
                open={dialogOpen}
                title="授权角色"
                width={842}
                onCancel={() => setDialogOpen(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <ZhButton onClick={() => setDialogOpen(false)}>取消</ZhButton>
                        <ZhButton type="primary" onClick={saveDialog}>
                            确认
                        </ZhButton>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <div className="flex gap-2">
                        <ZhInput
                            aria-label="角色名称搜索"
                            value={roleName}
                            placeholder="请输入角色名称"
                            onChange={event => setRoleName(event.target.value)}
                        />
                        <ZhButton onClick={() => void searchRoles()}>查询</ZhButton>
                    </div>
                    {roles.map(role => {
                        const id = getRoleId(role);
                        if (id == null) return null;
                        return (
                            <Checkbox
                                key={id}
                                checked={selectedIds.has(id)}
                                onChange={event => toggleRole(role, event.target.checked)}
                            >
                                {role.name || "--"}
                            </Checkbox>
                        );
                    })}
                    <div>
                        <div>已选择的角色</div>
                        {draftRoles.map(role => (
                            <Tag key={getRoleId(role)} closable onClose={() => removeRole(role)}>
                                {role.name || "--"}
                            </Tag>
                        ))}
                    </div>
                </div>
            </ZhDialog>
        </div>
    );
}

export default BackendAccountRoleSelector;
